import * as THREE from 'three/webgpu';
import type { FeatureFace } from '../../mesh/feature';
import { queryClosestPointOnlyOnFeatureFaceMeshViaBvh } from '../../mesh/feature/acceleration/featureFaceSpatialQuery';
import { collectFeatureFaceBoundaryWorldVertices } from './measureMeshFaceBoundaryVertices';

/** 默认单面采样点总上限。 */
const DEFAULT_FACE_SAMPLE_POINT_MAX = 128;

/** 默认单面边界顶点采样上限。 */
const DEFAULT_BOUNDARY_VERTEX_SAMPLE_MAX = 48;

/** 默认单面普通顶点采样上限。 */
const DEFAULT_VERTEX_SAMPLE_MAX = 32;

/** 默认单面三角形重心采样上限。 */
const DEFAULT_TRIANGLE_CENTROID_SAMPLE_MAX = 48;

/** 采样点去重坐标精度。 */
const SAMPLE_POINT_DEDUPLICATE_PRECISION = 6;

/**
 * 近似最近点采样来源。
 *
 * 设计说明：调用方可通过来源判断近似结果主要来自中心、边界还是面片内部采样，便于后续调试性能与精度。
 */
export type MeshFaceApproximateSampleSource =
    | 'center'
    | 'boundaryVertex'
    | 'meshVertex'
    | 'triangleCentroid';

/**
 * 两网格面近似最近点对。
 */
export interface MeshFaceApproximateClosestPointPair {
    /** 面 A 上的近似最近点（世界坐标）。 */
    pointOnA: THREE.Vector3;
    /** 面 B 上的近似最近点（世界坐标）。 */
    pointOnB: THREE.Vector3;
    /** 两点平方距离（世界单位平方），避免调用方重复计算。 */
    distanceSq: number;
    /** 触发当前最优结果的采样来源。 */
    sampleSource: MeshFaceApproximateSampleSource;
    /** 当前最优采样点是否来自面 A。 */
    sampleFromFaceA: boolean;
}

/**
 * 两网格面近似最近点求解参数。
 */
export interface MeshFaceApproximateClosestPointOptions {
    /** 单面参与 BVH 查询的采样点总上限。 */
    maxSamplePointsPerFace?: number;
    /** 单面边界顶点采样上限。 */
    maxBoundaryVerticesPerFace?: number;
    /** 单面普通网格顶点采样上限。 */
    maxVertexSamplesPerFace?: number;
    /** 单面三角形重心采样上限。 */
    maxTriangleCentroidsPerFace?: number;
    /** 最大搜索距离；超过该距离的 BVH 候选会被剪枝。 */
    maxThreshold?: number;
    /** 是否采样边界顶点；默认启用。 */
    includeBoundaryVertices?: boolean;
    /** 是否采样普通网格顶点；默认启用。 */
    includeVertexSamples?: boolean;
    /** 是否采样三角形重心；默认启用。 */
    includeTriangleCentroids?: boolean;
}

/**
 * 单个面上的近似查询采样点。
 */
interface ApproximateFaceSample {
    /** 采样点世界坐标。 */
    point: THREE.Vector3;
    /** 采样来源。 */
    source: MeshFaceApproximateSampleSource;
}

/**
 * 规整正整数配置。
 *
 * @param value 用户传入值。
 * @param fallback 默认值。
 * @returns 可用于循环上限的正整数。
 */
function normalizePositiveInteger(value: number | undefined, fallback: number): number {
    if (typeof value !== 'number' || !Number.isFinite(value)) {
        return fallback;
    }
    return Math.max(1, Math.floor(value));
}

/**
 * 判断布尔配置是否启用。
 *
 * @param value 用户传入值。
 * @returns 未显式关闭时为 `true`。
 */
function isOptionEnabled(value: boolean | undefined): boolean {
    return value !== false;
}

/**
 * 构建世界坐标采样点去重键。
 *
 * @param point 世界坐标点。
 * @returns 坐标去重键。
 */
function buildSamplePointKey(point: THREE.Vector3): string {
    return [
        point.x.toFixed(SAMPLE_POINT_DEDUPLICATE_PRECISION),
        point.y.toFixed(SAMPLE_POINT_DEDUPLICATE_PRECISION),
        point.z.toFixed(SAMPLE_POINT_DEDUPLICATE_PRECISION),
    ].join(':');
}

/**
 * 向采样列表追加不重复的世界坐标点。
 *
 * @param samples 采样列表。
 * @param usedKeys 已使用坐标键。
 * @param point 世界坐标点。
 * @param source 采样来源。
 * @param maxSampleCount 采样总上限。
 */
function appendUniqueSample(
    samples: ApproximateFaceSample[],
    usedKeys: Set<string>,
    point: THREE.Vector3,
    source: MeshFaceApproximateSampleSource,
    maxSampleCount: number,
): void {
    if (samples.length >= maxSampleCount) {
        return;
    }

    const key = buildSamplePointKey(point);
    if (usedKeys.has(key)) {
        return;
    }

    usedKeys.add(key);
    samples.push({
        point: point.clone(),
        source,
    });
}

/**
 * 读取三角索引对应的真实顶点索引。
 *
 * @param geometry 面代理几何。
 * @param cursor 顶点游标。
 * @param positionAttribute 位置属性。
 * @returns 真实顶点索引；越界时返回 `null`。
 */
function readGeometryVertexIndex(
    geometry: THREE.BufferGeometry,
    cursor: number,
    positionAttribute: THREE.BufferAttribute,
): number | null {
    const indexAttribute = geometry.getIndex();
    const vertexIndex = indexAttribute instanceof THREE.BufferAttribute
        ? Number(indexAttribute.getX(cursor))
        : cursor;

    if (!Number.isInteger(vertexIndex) || vertexIndex < 0 || vertexIndex >= positionAttribute.count) {
        return null;
    }
    return vertexIndex;
}

/**
 * 读取顶点世界坐标。
 *
 * @param positionAttribute 位置属性。
 * @param vertexIndex 顶点索引。
 * @param matrixWorld 世界矩阵。
 * @returns 顶点世界坐标。
 */
function readWorldVertex(
    positionAttribute: THREE.BufferAttribute,
    vertexIndex: number,
    matrixWorld: THREE.Matrix4,
): THREE.Vector3 {
    return new THREE.Vector3(
        positionAttribute.getX(vertexIndex),
        positionAttribute.getY(vertexIndex),
        positionAttribute.getZ(vertexIndex),
    ).applyMatrix4(matrixWorld);
}

/**
 * 收集稀疏网格顶点采样。
 *
 * @param face 面代理。
 * @param maxSampleCount 采样上限。
 * @returns 世界坐标顶点采样。
 */
function collectSparseMeshVertexSamples(
    face: FeatureFace,
    maxSampleCount: number,
): THREE.Vector3[] {
    const positionAttribute = face.geometry.getAttribute('position');
    if (!(positionAttribute instanceof THREE.BufferAttribute)) {
        return [];
    }

    face.updateWorldMatrix(true, false);
    const indexAttribute = face.geometry.getIndex();
    const vertexCursorCount = indexAttribute instanceof THREE.BufferAttribute
        ? indexAttribute.count
        : positionAttribute.count;
    if (vertexCursorCount <= 0) {
        return [];
    }

    const stride = Math.max(1, Math.ceil(vertexCursorCount / maxSampleCount));
    const samples: THREE.Vector3[] = [];
    const usedVertexIndices = new Set<number>();

    for (let cursor = 0; cursor < vertexCursorCount && samples.length < maxSampleCount; cursor += stride) {
        const vertexIndex = readGeometryVertexIndex(face.geometry, cursor, positionAttribute);
        if (vertexIndex === null || usedVertexIndices.has(vertexIndex)) {
            continue;
        }

        usedVertexIndices.add(vertexIndex);
        samples.push(readWorldVertex(positionAttribute, vertexIndex, face.matrixWorld));
    }

    return samples;
}

/**
 * 收集稀疏三角形重心采样。
 *
 * @param face 面代理。
 * @param maxSampleCount 采样上限。
 * @returns 世界坐标三角形重心采样。
 */
function collectSparseTriangleCentroidSamples(
    face: FeatureFace,
    maxSampleCount: number,
): THREE.Vector3[] {
    const positionAttribute = face.geometry.getAttribute('position');
    if (!(positionAttribute instanceof THREE.BufferAttribute)) {
        return [];
    }

    face.updateWorldMatrix(true, false);
    const indexAttribute = face.geometry.getIndex();
    const vertexCursorCount = indexAttribute instanceof THREE.BufferAttribute
        ? indexAttribute.count
        : positionAttribute.count;
    const triangleCount = Math.floor(vertexCursorCount / 3);
    if (triangleCount <= 0) {
        return [];
    }

    const stride = Math.max(1, Math.ceil(triangleCount / maxSampleCount));
    const samples: THREE.Vector3[] = [];

    for (let triangleIndex = 0; triangleIndex < triangleCount && samples.length < maxSampleCount; triangleIndex += stride) {
        const cursor = triangleIndex * 3;
        const indexA = readGeometryVertexIndex(face.geometry, cursor, positionAttribute);
        const indexB = readGeometryVertexIndex(face.geometry, cursor + 1, positionAttribute);
        const indexC = readGeometryVertexIndex(face.geometry, cursor + 2, positionAttribute);
        if (indexA === null || indexB === null || indexC === null) {
            continue;
        }

        /**
         * 重心采样能覆盖曲面内部区域，避免只看边界/顶点时漏掉凸曲面或凹曲面的内部近似最短距离。
         */
        const centroid = new THREE.Vector3(
            (
                positionAttribute.getX(indexA)
                + positionAttribute.getX(indexB)
                + positionAttribute.getX(indexC)
            ) / 3,
            (
                positionAttribute.getY(indexA)
                + positionAttribute.getY(indexB)
                + positionAttribute.getY(indexC)
            ) / 3,
            (
                positionAttribute.getZ(indexA)
                + positionAttribute.getZ(indexB)
                + positionAttribute.getZ(indexC)
            ) / 3,
        ).applyMatrix4(face.matrixWorld);

        samples.push(centroid);
    }

    return samples;
}

/**
 * 收集单面近似查询采样点。
 *
 * @param face 面代理。
 * @param options 近似求解参数。
 * @returns 受预算控制的采样点列表。
 */
function collectApproximateFaceSamples(
    face: FeatureFace,
    options: MeshFaceApproximateClosestPointOptions,
): ApproximateFaceSample[] {
    const maxSamplePoints = normalizePositiveInteger(
        options.maxSamplePointsPerFace,
        DEFAULT_FACE_SAMPLE_POINT_MAX,
    );
    const maxBoundaryVertices = normalizePositiveInteger(
        options.maxBoundaryVerticesPerFace,
        DEFAULT_BOUNDARY_VERTEX_SAMPLE_MAX,
    );
    const maxVertexSamples = normalizePositiveInteger(
        options.maxVertexSamplesPerFace,
        DEFAULT_VERTEX_SAMPLE_MAX,
    );
    const maxTriangleCentroids = normalizePositiveInteger(
        options.maxTriangleCentroidsPerFace,
        DEFAULT_TRIANGLE_CENTROID_SAMPLE_MAX,
    );

    const samples: ApproximateFaceSample[] = [];
    const usedKeys = new Set<string>();

    appendUniqueSample(samples, usedKeys, face.getWorldPoint(), 'center', maxSamplePoints);

    if (isOptionEnabled(options.includeBoundaryVertices)) {
        collectFeatureFaceBoundaryWorldVertices(face, maxBoundaryVertices).forEach((point) => {
            appendUniqueSample(samples, usedKeys, point, 'boundaryVertex', maxSamplePoints);
        });
    }

    if (isOptionEnabled(options.includeTriangleCentroids)) {
        collectSparseTriangleCentroidSamples(face, maxTriangleCentroids).forEach((point) => {
            appendUniqueSample(samples, usedKeys, point, 'triangleCentroid', maxSamplePoints);
        });
    }

    if (isOptionEnabled(options.includeVertexSamples)) {
        collectSparseMeshVertexSamples(face, maxVertexSamples).forEach((point) => {
            appendUniqueSample(samples, usedKeys, point, 'meshVertex', maxSamplePoints);
        });
    }

    return samples;
}

/**
 * 选择距离更小的近似最近点对。
 *
 * @param current 当前最优点对。
 * @param candidate 候选点对。
 * @returns 更优点对。
 */
function pickCloserApproximatePair(
    current: MeshFaceApproximateClosestPointPair | null,
    candidate: MeshFaceApproximateClosestPointPair | null,
): MeshFaceApproximateClosestPointPair | null {
    if (!candidate) {
        return current;
    }
    if (!current || candidate.distanceSq < current.distanceSq) {
        return candidate;
    }
    return current;
}

/**
 * 解析 BVH 查询剪枝距离。
 *
 * @param best 当前最优近似点对。
 * @param options 近似求解参数。
 * @returns 可传入 BVH 的最大搜索距离。
 */
function resolveBvhMaxThreshold(
    best: MeshFaceApproximateClosestPointPair | null,
    options: MeshFaceApproximateClosestPointOptions,
): number | undefined {
    const optionThreshold = typeof options.maxThreshold === 'number' && Number.isFinite(options.maxThreshold)
        ? Math.max(0, options.maxThreshold)
        : Number.POSITIVE_INFINITY;
    const bestThreshold = best ? Math.sqrt(best.distanceSq) : Number.POSITIVE_INFINITY;
    const threshold = Math.min(optionThreshold, bestThreshold);
    return Number.isFinite(threshold) ? threshold : undefined;
}

/**
 * 使用 BVH 求世界坐标点到目标面三角网格的最近点。
 *
 * 设计说明：
 * 1. 近似面↔面测距只需要最近点，不需要法向；
 * 2. 这里复用测量专用 point-only BVH 出口，避免有效最近点被法向计算误判丢弃；
 * 3. 公共出口统一维护 BVH 构建与坐标转换，防止不同测量链路出现行为差异。
 *
 * @param targetFace 目标面。
 * @param queryPointWorld 查询点世界坐标。
 * @param maxThreshold 可选最大搜索距离。
 * @returns 目标面上的世界坐标最近点；无法查询时返回 `null`。
 */
function queryClosestPointOnFeatureFaceMeshPointOnlyViaBvh(
    targetFace: FeatureFace,
    queryPointWorld: THREE.Vector3,
    maxThreshold?: number,
): THREE.Vector3 | null {
    const projection = queryClosestPointOnlyOnFeatureFaceMeshViaBvh(targetFace, queryPointWorld, { maxThreshold });
    return projection?.point.clone() ?? null;
}

/**
 * 从某一侧采样点向另一侧面执行 BVH 最近点查询。
 *
 * @param sample 采样点。
 * @param targetFace 目标面。
 * @param sampleFromFaceA 采样点是否来自面 A。
 * @param options 近似求解参数。
 * @param best 当前最优点对。
 * @returns 候选近似最近点对。
 */
function queryApproximatePairFromSample(
    sample: ApproximateFaceSample,
    targetFace: FeatureFace,
    sampleFromFaceA: boolean,
    options: MeshFaceApproximateClosestPointOptions,
    best: MeshFaceApproximateClosestPointPair | null,
): MeshFaceApproximateClosestPointPair | null {
    const maxThreshold = resolveBvhMaxThreshold(best, options);
    const closestPoint = queryClosestPointOnFeatureFaceMeshPointOnlyViaBvh(targetFace, sample.point, maxThreshold);
    if (!closestPoint) {
        return null;
    }

    const pointOnA = sampleFromFaceA ? sample.point.clone() : closestPoint.clone();
    const pointOnB = sampleFromFaceA ? closestPoint.clone() : sample.point.clone();

    return {
        pointOnA,
        pointOnB,
        distanceSq: pointOnA.distanceToSquared(pointOnB),
        sampleSource: sample.source,
        sampleFromFaceA,
    };
}

/**
 * 使用 BVH 点到三角网格查询近似求解两个复杂曲面之间的最近点。
 *
 * 设计说明：
 * 1. 该函数只提供可控预算的近似结果，不替代现有精确/精化链路；
 * 2. 每个采样点都会通过目标面的 BVH `closestPointToPoint` 查询落到真实三角形最近点上；
 * 3. 双向查询可减少单侧采样遗漏，提高复杂曲面、边界曲面与局部凹凸形态下的稳定性；
 * 4. 当前模块不接入测量主流程，后续可作为 `computeClosestPointsBetweenMeshFaces` 的快速 seed。
 *
 * @param faceA 面 A。
 * @param faceB 面 B。
 * @param options 近似求解参数。
 * @returns 两面近似最近点对；无法求解时返回 `null`。
 */
export function computeApproximateClosestPointsBetweenMeshFaces(
    faceA: FeatureFace,
    faceB: FeatureFace,
    options: MeshFaceApproximateClosestPointOptions = {},
): MeshFaceApproximateClosestPointPair | null {
    const samplesA = collectApproximateFaceSamples(faceA, options);
    const samplesB = collectApproximateFaceSamples(faceB, options);
    if (samplesA.length <= 0 || samplesB.length <= 0) {
        return null;
    }

    let best: MeshFaceApproximateClosestPointPair | null = null;

    /**
     * 先从 A 查 B，再从 B 查 A。距离阈值会随 best 收紧，使后续 BVH 查询能更早剪枝。
     */
    samplesA.forEach((sample) => {
        best = pickCloserApproximatePair(
            best,
            queryApproximatePairFromSample(sample, faceB, true, options, best),
        );
    });

    samplesB.forEach((sample) => {
        best = pickCloserApproximatePair(
            best,
            queryApproximatePairFromSample(sample, faceA, false, options, best),
        );
    });

    return best;
}
